import { SECURITY_EVENTS, EVENT_SEVERITY } from './constants';

function resolveGetDisplayMedia() {
  if (typeof navigator === 'undefined') return null;
  const md = navigator.mediaDevices;
  if (md && typeof md.getDisplayMedia === 'function') {
    return (constraints) => md.getDisplayMedia(constraints);
  }
  if (typeof navigator.getDisplayMedia === 'function') {
    return (constraints) => navigator.getDisplayMedia(constraints);
  }
  return null;
}

export function describeScreenShareError(err) {
  const name = err && err.name;
  const message = (err && err.message) || '';
  if (name === 'NotAllowedError' || name === 'AbortError') {
    return 'Screen sharing was cancelled. Share your entire screen to continue.';
  }
  if (name === 'NotFoundError') {
    return 'No screen is available to share. Connect a display and try again.';
  }
  if (name === 'NotSupportedError' || /not supported|unavailable/i.test(message)) {
    return 'This browser could not start screen sharing with the requested settings. Use current Chrome or Edge on https (or localhost), then try again.';
  }
  if (name === 'InvalidStateError' || name === 'NotReadableError') {
    return 'Screen sharing could not start. Close other screen-share sessions and try again.';
  }
  return message || 'Screen sharing could not start. Please try again.';
}

export async function startScreenCapture() {
  const getDisplayMedia = resolveGetDisplayMedia();
  if (!getDisplayMedia) {
    const err = new Error('Display Media API unavailable');
    err.name = 'NotSupportedError';
    throw err;
  }

  const attempts = [
    { video: true, audio: false },
    { video: { frameRate: { ideal: 10, max: 15 } }, audio: false },
    { video: true }
  ];

  let lastError = null;
  for (let i = 0; i < attempts.length; i += 1) {
    try {
      return await getDisplayMedia(attempts[i]);
    } catch (err) {
      lastError = err;
      const retryable = err && (err.name === 'NotSupportedError' || err.name === 'OverconstrainedError' || err.name === 'TypeError');
      if (!retryable || i === attempts.length - 1) throw err;
    }
  }
  throw lastError;
}

export function inspectDisplaySurface(stream) {
  const track = stream && stream.getVideoTracks && stream.getVideoTracks()[0];
  if (!track || !track.getSettings) {
    return { displaySurface: null, verified: false };
  }
  const settings = track.getSettings() || {};
  return {
    displaySurface: settings.displaySurface || null,
    verified: !!settings.displaySurface,
    preferMonitor: settings.displaySurface === 'monitor'
  };
}

export function captureJpegFromStream(stream, { maxWidth = 960, quality = 0.55 } = {}) {
  return new Promise((resolve) => {
    if (!stream || typeof document === 'undefined') {
      resolve(null);
      return;
    }
    const video = document.createElement('video');
    video.muted = true;
    video.playsInline = true;
    video.setAttribute('playsinline', 'true');
    video.srcObject = stream;
    const finish = () => {
      try {
        const w = video.videoWidth || 640;
        const h = video.videoHeight || 360;
        const width = Math.min(maxWidth, w);
        const height = Math.max(1, Math.round(width * (h / w || 0.56)));
        const canvas = document.createElement('canvas');
        canvas.width = width;
        canvas.height = height;
        const ctx = canvas.getContext('2d');
        if (!ctx) {
          resolve(null);
          return;
        }
        ctx.drawImage(video, 0, 0, width, height);
        resolve(canvas.toDataURL('image/jpeg', quality));
      } catch (_) {
        resolve(null);
      } finally {
        try {
          video.pause();
          video.srcObject = null;
        } catch (_) { /* ignore */ }
      }
    };
    video.onloadeddata = () => {
      video.play().then(finish).catch(() => resolve(null));
    };
    video.onerror = () => resolve(null);
  });
}

export function subscribeTrackEnded(stream, onEnded) {
  const track = stream && stream.getVideoTracks && stream.getVideoTracks()[0];
  if (!track) return () => {};
  const handler = () => onEnded();
  track.addEventListener('ended', handler);
  return () => track.removeEventListener('ended', handler);
}

export function stopScreenCapture(stream) {
  if (!stream) return;
  stream.getTracks().forEach((t) => {
    try {
      t.stop();
    } catch (_) {
      /* ignore */
    }
  });
}

export function screenShareDeniedEvent() {
  return {
    type: SECURITY_EVENTS.SCREEN_SHARE_DENIED,
    severity: EVENT_SEVERITY[SECURITY_EVENTS.SCREEN_SHARE_DENIED],
    message: 'Candidate denied or dismissed screen sharing'
  };
}

const PREFERRED_MIME = ['video/webm;codecs=vp9', 'video/webm;codecs=vp8', 'video/webm'];

export function supportedRecorderMime() {
  if (typeof MediaRecorder === 'undefined') return '';
  return PREFERRED_MIME.find((t) => MediaRecorder.isTypeSupported(t)) || '';
}

export function startChunkRecorder(stream, onChunk, timesliceMs = 8000) {
  const mimeType = supportedRecorderMime();
  if (!mimeType || typeof MediaRecorder === 'undefined') {
    return { recorder: null, stop: async () => {} };
  }
  const recorder = new MediaRecorder(stream, { mimeType });
  recorder.ondataavailable = (ev) => {
    if (ev.data && ev.data.size > 0 && onChunk) onChunk(ev.data);
  };
  recorder.start(timesliceMs);
  return {
    recorder,
    stop: () =>
      new Promise((resolve) => {
        if (recorder.state === 'inactive') {
          resolve();
          return;
        }
        recorder.onstop = () => resolve();
        recorder.stop();
      })
  };
}
